import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {SegmentedControl, PredefinedNotifierType} from 'components';
import {ClientContext} from 'context';
import {useApiData, useClientSession} from 'hooks';
import {SessionStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionFlowMode, PreSessionFlowState} from 'types';
import {sessionUtils} from 'utils';

import './styles.scss';

export const PreSessionModeSwitcher = ({busy = false}: {busy?: boolean}) => {
    const {t} = useTranslation();
    const {session, textChatActivity, avatarActivity} = useClientSession();
    const {textChatInputDisabled} = useContext(ClientContext);
    const snapshot = useMemo(() => ((textChatActivity || avatarActivity)?.data as
        {preSessionFlow?: PreSessionFlowState})?.preSessionFlow, [textChatActivity, avatarActivity]);
    const participantId = useMemo(() => sessionUtils.getClientParticipant(session)?.id, [session]);
    const {send, loading} = useApiData(clientActionsApi.setPreSessionFlowMode, {
        notificationType: PredefinedNotifierType.SaveError,
    });
    const handleChange = useCallback((mode: PreSessionFlowMode) => {
        if (session?.id && participantId && mode !== (snapshot?.activeMode ?? PreSessionFlowMode.Chatbot)) {
            send({sessionId: session.id, participantId}, {mode});
        }
    }, [send, session?.id, participantId, snapshot?.activeMode]);
    const activeMode = useMemo(() => snapshot?.activeMode ?? PreSessionFlowMode.Chatbot, [snapshot?.activeMode]);
    const options = useMemo(() => [
        {value: PreSessionFlowMode.Chatbot, label: t(TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW.MODE_CHATBOT)},
        {value: PreSessionFlowMode.Form, label: t(TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW.MODE_FORM)},
    ], [t]);
    if (![SessionStatus.PreCall, SessionStatus.Chatbot].includes(session?.status)
        || !snapshot?.flowSnapshot.modes?.chatbot || !snapshot.flowSnapshot.modes.form
        || snapshot.completedInChat || snapshot.awaitingConfirmation) {
        return null;
    }
    return (
        <div className='vs-pre-session-mode-switcher'>
            <span>{t(TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW.MODE)}</span>
            <SegmentedControl
                options={options}
                value={activeMode}
                ariaLabel={t(TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW.MODE)}
                disabled={busy || loading || textChatInputDisabled}
                onChange={handleChange}
            />
        </div>
    );
};
